/**
 * プライベートメールアドレス（渡す相手ごとに発行する使い捨て転送アドレス）。
 *
 * ## この画面の目的
 *
 * 「アドレスをたくさん作れる」画面ではない。**迷惑メールが来た瞬間に
 * そのアドレスだけを止められる**ことが価値の中心で、止めた時点で
 * 「どのサービスが名簿を漏らしたか」が確定する。したがって:
 *
 *   - **ラベル（渡す相手）を必須**にする。ラベルが無いと、届いた迷惑メールから
 *     漏洩元をたどれず、機能そのものが意味を失う。
 *   - **停止は 1 クリック**にする（確認ダイアログを挟まない）。理由は下の
 *     `RowActions` のコメント。
 *   - 発行直後に**アドレスをコピーできる**こと。相手のフォームへ貼るために
 *     発行するので、ダイアログを閉じてから一覧で探させない。
 *
 * ## 上限 0 の意味が他の機能と逆であること
 *
 * `planLimits.privateAddressesMax` は **0 が「無制限」ではなく「使えない」**。
 * `<PlanLimitAlert>` / `formatLimit()` は 0 を無制限として扱うので、
 * **この画面ではそのまま使えない**（ライトプランに「無制限」と出てしまう）。
 * 判定は `@mail-sv/shared` の `isPrivateAddressEnabled()` /
 * `privateAddressesRemaining()` を通す。上限に達したとき（上限 > 0）だけ
 * `<PlanLimitAlert>` に寄せて、他の画面と同じ見た目にする。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CopyableCode,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatCard,
  StatusBadge,
  Textarea,
  UsageBar,
  formatCount,
  formatDateTime,
  useCursorPagination,
  useToast,
  type DataTableColumn,
} from '@mail-sv/ui';
import {
  ORG_PERMISSION,
  PRIVATE_ADDRESS_LOCAL_PART_LENGTH,
  PRIVATE_ADDRESS_LOCAL_PART_MAX,
  PRIVATE_ADDRESS_LOCAL_PART_MIN,
  PRIVATE_ADDRESS_LABEL_MAX,
  PRIVATE_ADDRESS_NOTE_MAX,
  isPrivateAddressEnabled,
  type PrivateAddressStatus,
  type PrivateAddressView,
} from '@mail-sv/shared';
import {
  ArrowUpRight,
  Flame,
  Pencil,
  PlayCircle,
  Plus,
  ShieldOff,
  Trash2,
  VenetianMask,
} from 'lucide-react';
import { api, newIdempotencyKey } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { Disclosure, HelpNote, StepList } from '../components/help.js';
import { JobProgressBar, useJobProgress } from '../components/job-progress.js';
import { PlanLimitAlert } from '../components/plan-limit.js';
import { canManageOrg, useAuth, usePermission } from '../stores/auth.js';
import type {
  CursorPage,
  Mailbox,
  PrivateAddressAccepted,
  PrivateAddressCreated,
  PrivateAddressPage,
} from '../lib/api-types.js';

/* ================================================================ 画面本体 */

export function PrivateAddressesPage() {
  const org = useOrg();
  const { user } = useAuth();
  const canWrite = usePermission(ORG_PERMISSION.mailboxWrite);
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<PrivateAddressView | null>(null);
  const [status, setStatus] = React.useState<'all' | PrivateAddressStatus>('all');
  const [labelInput, setLabelInput] = React.useState('');
  const label = useDebounced(labelInput.trim(), 350);
  const page = useCursorPagination(50);

  const filter = { status, label, cursor: page.cursor, limit: page.limit };

  const list = useQuery({
    queryKey: qk.privateAddresses(filter),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      if (status !== 'all') params.set('status', status);
      if (label !== '') params.set('label', label);
      return api.get<PrivateAddressPage>(`/private-addresses?${params.toString()}`);
    },
  });

  const invalidate = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: qk.privateAddressesAll() });
  }, [queryClient]);

  /*
   * **状態変更は 202 + `{jobId}`。** ノードの配送表から消える（＝ 550 が返る）
   * までにわずかな時間があるので、返ってきた jobId を追って
   * 「反映しています」を出す。追わないと、止めた直後に届いた 1 通で
   * 「止めたのに届く」と誤解される。
   */
  const [jobId, setJobId] = React.useState<string | null>(null);
  const { job } = useJobProgress(jobId);

  const disable = useMutation({
    mutationFn: (args: { id: string; burn: boolean }) =>
      api.post<PrivateAddressAccepted>(
        `/private-addresses/${encodeURIComponent(args.id)}/disable`,
        { burn: args.burn },
      ),
    onSuccess: (res) => {
      setJobId(res.jobId);
      invalidate();
    },
  });

  const enable = useMutation({
    mutationFn: (id: string) =>
      api.post<PrivateAddressAccepted>(
        `/private-addresses/${encodeURIComponent(id)}/enable`,
        {},
      ),
    onSuccess: (res) => {
      setJobId(res.jobId);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.del<PrivateAddressAccepted>(`/private-addresses/${encodeURIComponent(id)}`),
    onSuccess: (res) => {
      setJobId(res.jobId);
      invalidate();
    },
  });

  const summary = list.data?.summary;
  /*
   * 上限はサーバーの応答（`summary.limit`）を第一の出典にする。
   * 一覧がまだ返っていない初回描画のあいだだけ `/org` のプラン情報で代用する
   * （どちらも同じ `PLAN_CATALOG` 由来なので値は一致する）。
   */
  const limit = summary?.limit ?? org.data?.plan.limits.privateAddressesMax ?? 0;
  const enabled = isPrivateAddressEnabled(limit);

  const columns: Array<DataTableColumn<PrivateAddressView>> = [
    {
      id: 'label',
      header: '渡した相手（ラベル）',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.label}</p>
          {row.note ? (
            <p className="truncate text-xs text-muted-foreground">{row.note}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'address',
      header: 'アドレス',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-mono text-xs">{row.address}</p>
          <p className="truncate text-xs text-muted-foreground">
            届く先: {row.mailboxAddress || '（削除済み）'}
          </p>
        </div>
      ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-32',
      cell: (row) => (
        <div className="space-y-1">
          <StatusBadge status={row.status} dot size="sm" />
          {row.status !== 'active' && row.disabledReason ? (
            <p className="truncate text-xs text-muted-foreground">{row.disabledReason}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'received',
      header: '受信 / 迷惑',
      width: 'w-28',
      align: 'right',
      hideBelow: 'md',
      cell: (row) => (
        <span className="tabular text-xs">
          {formatCount(row.receivedCount)} / {formatCount(row.spamCount)}
        </span>
      ),
    },
    {
      id: 'lastReceivedAt',
      header: '最終受信',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-xs text-muted-foreground">
          {row.lastReceivedAt ? formatDateTime(row.lastReceivedAt) : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-64',
      align: 'right',
      cell: (row) =>
        canWrite ? (
          <RowActions
            row={row}
            onDisable={(burn) => disable.mutate({ id: row.id, burn })}
            onEnable={() => enable.mutate(row.id)}
            onEdit={() => setEditing(row)}
            onDelete={() => remove.mutate(row.id)}
            pending={
              (disable.isPending && disable.variables?.id === row.id) ||
              (enable.isPending && enable.variables === row.id) ||
              (remove.isPending && remove.variables === row.id)
            }
          />
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="プライベートメールアドレス"
        description="サービスごとに違うアドレスを発行して渡し、本来のアドレスを知られないようにします。迷惑メールが来たら、そのアドレスだけを止められます。"
      />

      <div className="space-y-5">
        <UsageHelp />

        {!enabled ? (
          <PlanUpsell canUpgrade={canManageOrg(user)} />
        ) : (
          <>
            <SummaryCards summary={summary} limit={limit} />

            {/* 上限に達したときだけ、他の画面と同じ告知を出す（上限 > 0 のときのみ）。 */}
            <PlanLimitAlert
              resourceLabel="プライベートメールアドレス"
              current={summary?.total ?? 0}
              max={limit}
              canUpgrade={canManageOrg(user)}
            />

            {jobId ? (
              <Card>
                <CardContent className="pt-5">
                  <JobProgressBar job={job} />
                </CardContent>
              </Card>
            ) : null}

            {disable.isError ? <FormAlert error={disable.error} /> : null}
            {enable.isError ? <FormAlert error={enable.error} /> : null}
            {remove.isError ? <FormAlert error={remove.error} /> : null}

            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <Field label="状態" className="w-40">
                  <Select
                    value={status}
                    onValueChange={(v) => {
                      setStatus(v as 'all' | PrivateAddressStatus);
                      page.reset();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">すべて</SelectItem>
                      <SelectItem value="active">有効</SelectItem>
                      <SelectItem value="disabled">無効</SelectItem>
                      <SelectItem value="burned">漏洩により破棄</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="渡した相手で探す" className="w-56" htmlFor="pa-label-filter">
                  <Input
                    id="pa-label-filter"
                    value={labelInput}
                    onChange={(e) => {
                      setLabelInput(e.target.value);
                      page.reset();
                    }}
                    placeholder="例: 〇〇通販"
                  />
                </Field>
              </div>

              {canWrite ? (
                <Button
                  onClick={() => setCreateOpen(true)}
                  disabled={(summary?.remaining ?? 1) <= 0}
                >
                  <Plus aria-hidden />
                  アドレスを発行
                </Button>
              ) : null}
            </div>

            <DataTable
              columns={columns}
              rows={list.data?.items ?? []}
              rowKey={(row) => row.id}
              loading={list.isPending}
              error={list.isError ? 'プライベートアドレスを取得できませんでした。' : undefined}
              empty={
                <EmptyState
                  icon={VenetianMask}
                  title="まだ発行していません"
                  description="通販サイトや会員登録など、本来のアドレスを渡したくない相手ごとに 1 本ずつ発行します。迷惑メールが来たら、その 1 本だけを止められます。"
                  action={
                    canWrite ? (
                      <Button onClick={() => setCreateOpen(true)}>
                        <Plus aria-hidden />
                        アドレスを発行
                      </Button>
                    ) : null
                  }
                />
              }
              footer={
                <CursorPagination
                  nextCursor={list.data?.nextCursor ?? null}
                  canGoPrev={page.canGoPrev}
                  onNext={() => page.goNext(list.data?.nextCursor ?? null)}
                  onPrev={page.goPrev}
                  limit={page.limit}
                  onLimitChange={page.setLimit}
                  currentCount={list.data?.items.length}
                  loading={list.isFetching}
                />
              }
            />
          </>
        )}
      </div>

      <CreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        remaining={summary?.remaining ?? 0}
      />
      {editing ? <EditDialog row={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

/* ============================================================ 説明・上限 */

/**
 * 使い方。**契約者パネルには専用のヘルプ画面が無い**（ヘッダの「ヘルプ」は
 * `VITE_SUPPORT_URL` の外部サイトへ出る導線で、SPA 内には記事を持たない）。
 * したがって使い方はこの画面の中に置く。他の画面（`aliases.tsx` の
 * 「エイリアスと転送のちがい」など）と同じ作法。
 */
function UsageHelp() {
  return (
    <div className="space-y-3">
      <HelpNote title="本来のアドレスを渡さずにメールを受け取る仕組みです">
        <p>
          通販サイトや会員登録など、相手ごとに違うアドレスを発行して渡します。
          そこへ届いたメールは、いつものメールアドレスにそのまま届きます。
          迷惑メールが来たら、<strong>そのアドレスだけを止める</strong>だけで済み、
          本来のアドレスは何の影響も受けません。
        </p>
      </HelpNote>

      <Disclosure summary="使い方（3 ステップ）と、知っておくとよいこと">
        <div className="space-y-4">
          <StepList
            steps={[
              '「アドレスを発行」を押し、渡す相手の名前（ラベル）を入力します。ラベルは必須です。後から迷惑メールが来たときに、どの相手が漏らしたのかを特定する唯一の手がかりになります。',
              '発行されたアドレスをコピーして、相手の入力フォームに貼り付けます。発行した直後の画面でコピーできます。',
              '迷惑メールが届いたら、その行の「止める」を押します。1 クリックで止まり、以降そのアドレス宛のメールは受け取りを拒否します。',
            ]}
          />
          <ul className="list-disc space-y-1.5 pl-4">
            <li>
              止めたアドレス宛のメールは<strong>受け取りを拒否</strong>します
              （黙って捨てません）。送った相手にはエラーが返るので、
              正当な相手が「送ったのに届かない」まま気づかない、ということが起きません。
            </li>
            <li>
              「止める」と「漏洩により破棄」は、メールが届かなくなる点では同じです。
              分けているのは<strong>記録のため</strong>です。
              「使い終わったので止めた」と「迷惑メールが来たので捨てた」を同じ扱いにすると、
              どの相手が名簿を漏らしたのかが後から分からなくなります。
            </li>
            <li>
              止めたアドレスは「戻す」でいつでも再開できます。削除すると発行の枠が戻りますが、
              同じアドレスは二度と作れません。
            </li>
            <li>
              Webメールでは、発行したアドレスが差出人（From）の候補に自動で追加されます。
              返信するときにこのアドレスを選べば、相手に本来のアドレスは伝わりません。
            </li>
          </ul>
          {/*
            **実装が伴っていないことを画面で黙らない。** 受信数・迷惑メール数の
            集計はノード側からの報告が未実装で、現状は常に 0 のまま。
            数字が出ている以上、説明が無いと「受信していないのに届いている」と
            受け取られて問い合わせになる。
          */}
          <p className="text-xs">
            ※「受信 / 迷惑」の件数と「最終受信」は、集計の準備が整うまで 0 のまま表示されます。
            アドレスの発行・停止・受信そのものはこの表示に関係なく動作します。
          </p>
        </div>
      </Disclosure>
    </div>
  );
}

/**
 * プランで使えないとき（上限 0）の案内。
 *
 * **`<PlanLimitAlert>` を使わない。** あちらは `max === 0` を無制限として
 * 扱うので何も表示されず、ライトプランの契約者には「表が空なだけの画面」に
 * 見える。0 は「使えない」であることを言い切る。
 */
function PlanUpsell({ canUpgrade }: { canUpgrade: boolean }) {
  return (
    <Alert variant="info">
      <AlertTitle>ご利用中のプランではお使いいただけません</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          プライベートメールアドレスはスタンダードプラン以上でご利用いただけます。
          {canUpgrade
            ? 'プランを変更すると、すぐに発行できるようになります。'
            : '組織の管理者にプラン変更をご依頼ください。'}
        </p>
        {canUpgrade ? (
          <Button asChild variant="outline" size="sm">
            <AppLink to="/billing/plan">
              プランを変更する
              <ArrowUpRight aria-hidden />
            </AppLink>
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

function SummaryCards({
  summary,
  limit,
}: {
  summary: PrivateAddressPage['summary'] | undefined;
  limit: number;
}) {
  const total = summary?.total ?? 0;
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="有効"
          value={formatCount(summary?.active ?? 0)}
          unit="件"
          icon={VenetianMask}
          hint="いま受け取れるアドレスの数です。"
        />
        <StatCard
          label="停止中"
          value={formatCount(summary?.disabled ?? 0)}
          unit="件"
          tone="warning"
        />
        <StatCard
          label="漏洩により破棄"
          value={formatCount(summary?.burned ?? 0)}
          unit="件"
          tone="destructive"
          icon={Flame}
          hint="迷惑メールが届いたので捨てたアドレスの数です。"
        />
        <StatCard
          label="受信 / 迷惑メール"
          value={`${formatCount(summary?.receivedTotal ?? 0)} / ${formatCount(summary?.spamTotal ?? 0)}`}
          hint="集計の準備が整うまで 0 のまま表示されます。"
        />
      </div>

      {/*
        **`<UsageBar>` を `<StatCard hint>` に入れない。** あちらの `hint` は
        `<p>` の中に描画されるので、`<div>` を返すこの部品を渡すと
        「`<p>` に `<div>` は入れられない」で React が毎回 error を吐く
        （画面は動くが、本当のエラーがコンソールで埋もれる）。
      */}
      <UsageBar
        used={total}
        total={limit}
        label="発行数（プラン上限）"
        formatValue={(n) => `${formatCount(n)} 件`}
      />
    </div>
  );
}

/* ================================================================ 行の操作 */

/**
 * 行のアクション。
 *
 * **「止める」と「漏洩により破棄」には確認ダイアログを挟まない。**
 * 破壊的操作は確認を通すのがこのアプリの規約だが、ここは 2 つの理由で例外にする。
 *
 *   1. **可逆である。** 押し間違えても「戻す」1 クリックで元に戻り、
 *      その間に失われるのは「止めていた数秒間に届いたはずのメール」だけ。
 *   2. **速さそのものが機能の価値。** 迷惑メールが来た瞬間に止められることが
 *      この機能の存在理由で、そこに確認を挟むと「後でやろう」になり、
 *      結局止めないまま迷惑メールを受け続ける。
 *
 * 逆に**削除は確認を通す**。削除すると同じアドレスを二度と作れず、
 * 相手に渡した紙やフォームに残ったアドレスが恒久的に無効になる。
 */
function RowActions({
  row,
  onDisable,
  onEnable,
  onEdit,
  onDelete,
  pending,
}: {
  row: PrivateAddressView;
  onDisable: (burn: boolean) => void;
  onEnable: () => void;
  onEdit: () => void;
  onDelete: () => void;
  pending: boolean;
}) {
  const toast = useToast();
  const copy = () => {
    void navigator.clipboard
      .writeText(row.address)
      .then(() => toast.success('アドレスをコピーしました'))
      .catch(() => toast.error('コピーできませんでした', '手動で選択してコピーしてください。'));
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      <Button variant="ghost" size="sm" onClick={copy}>
        コピー
      </Button>
      {row.status === 'active' ? (
        <>
          <Button variant="ghost" size="sm" loading={pending} onClick={() => onDisable(false)}>
            <ShieldOff aria-hidden />
            止める
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={pending}
            title="迷惑メールが届いたので捨てる（どの相手が漏らしたかの記録に残ります）"
            onClick={() => onDisable(true)}
          >
            <Flame aria-hidden />
            <span className="sr-only">迷惑メールが届いたので捨てる</span>
          </Button>
        </>
      ) : (
        <Button variant="ghost" size="sm" loading={pending} onClick={onEnable}>
          <PlayCircle aria-hidden />
          戻す
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={onEdit}>
        <Pencil aria-hidden />
        <span className="sr-only">ラベルとメモを変更</span>
      </Button>
      <ConfirmDialogButton
        title={`${row.address} を削除します`}
        description="このアドレスは二度と作れなくなります。相手に渡したフォームや書類に書いたアドレスも無効になります。一時的に止めたいだけなら「止める」をお使いください。"
        confirmLabel="削除する"
        pending={pending}
        onConfirm={onDelete}
      >
        <Trash2 aria-hidden />
        <span className="sr-only">削除</span>
      </ConfirmDialogButton>
    </div>
  );
}

/* ================================================================== 発行 */

/**
 * 発行ダイアログ。
 *
 * **`Idempotency-Key` を必ず付ける。** サーバー側は「同じキーの再送では
 * 二重発行しない」を Redis と DB の 2 段で守っているが、鍵を送らなければ
 * どちらも働かない。通信が切れて画面が再送したときに 2 本発行されると、
 * プランの枠を空振りで 1 つ食う。
 *
 * **ローカルパートは指定できない**（長さだけ）。好きな文字列を取れるなら
 * エイリアスと同じもので、「推測できない」という前提が利用者の命名習慣で崩れる。
 */
function CreateDialog({
  open,
  onOpenChange,
  remaining,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  remaining: number;
}) {
  const queryClient = useQueryClient();
  const [mailboxId, setMailboxId] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [note, setNote] = React.useState('');
  const [length, setLength] = React.useState(PRIVATE_ADDRESS_LOCAL_PART_LENGTH);
  const [created, setCreated] = React.useState<PrivateAddressCreated | null>(null);
  const idemKey = React.useRef(newIdempotencyKey());

  const mailboxes = useQuery({
    queryKey: qk.mailboxes({ forPrivateAddress: true }),
    queryFn: () => api.get<CursorPage<Mailbox>>('/mailboxes?limit=200'),
    enabled: open,
  });
  // 依存に空配列リテラルが毎回入ると、下の初期選択の effect が毎描画で走る。
  const mailboxItems = React.useMemo(() => mailboxes.data?.items ?? [], [mailboxes.data]);

  React.useEffect(() => {
    if (!open) return;
    setLabel('');
    setNote('');
    setLength(PRIVATE_ADDRESS_LOCAL_PART_LENGTH);
    setCreated(null);
    // **再送用の鍵は「ダイアログを開くたび」に引き直す。** mutation の
    // 再試行では同じ値を送り続ける必要があるので、送信のたびには変えない。
    idemKey.current = newIdempotencyKey();
  }, [open]);

  // メールボックスが 1 件だけなら選ばせない（大半の個人利用がこれ）。
  React.useEffect(() => {
    if (!open) return;
    if (mailboxId === '' && mailboxItems.length > 0) setMailboxId(mailboxItems[0]!.id);
  }, [open, mailboxId, mailboxItems]);

  const create = useMutation({
    mutationFn: () =>
      api.post<PrivateAddressCreated>(
        '/private-addresses',
        {
          mailboxId,
          label: label.trim(),
          ...(note.trim() === '' ? {} : { note: note.trim() }),
          ...(length === PRIVATE_ADDRESS_LOCAL_PART_LENGTH ? {} : { localPartLength: length }),
        },
        { idempotencyKey: idemKey.current },
      ),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: qk.privateAddressesAll() });
      setCreated(res);
    },
  });

  const { job } = useJobProgress(created?.jobId ?? null);
  const labelOk = label.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(next) => (create.isPending ? undefined : onOpenChange(next))}>
      <DialogContent className="max-w-lg">
        {created ? (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle>アドレスを発行しました</DialogTitle>
              <DialogDescription>
                このアドレスを相手の入力フォームに貼り付けてください。
              </DialogDescription>
            </DialogHeader>

            {/* **閉じる前にコピーさせる。** 一覧からも取れるが、
                発行の目的は「いま貼る」ことなので導線をここで完結させる。 */}
            <CopyableCode value={created.address} label={`「${created.label}」に渡すアドレス`} />

            <p className="text-sm text-muted-foreground">
              このアドレス宛のメールは <strong>{created.mailboxAddress}</strong> に届きます。
              迷惑メールが来たら、一覧の「止める」でこのアドレスだけを止められます。
            </p>

            {created.jobId ? <JobProgressBar job={job} /> : null}
            <p className="text-xs text-muted-foreground">
              サーバーへの反映は通常 1 分以内に完了します。反映前に届いたメールは受け取れません。
            </p>

            <div className="flex justify-end">
              <Button type="button" onClick={() => onOpenChange(false)}>
                閉じる
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (labelOk && mailboxId !== '') create.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>プライベートアドレスを発行</DialogTitle>
              <DialogDescription>
                相手ごとに違うアドレスを作ります。アドレスの文字列は自動で決まります。
              </DialogDescription>
            </DialogHeader>

            <FormAlert error={create.error} />

            <Field
              label="渡す相手（ラベル）"
              htmlFor="pa-label"
              required
              hint="迷惑メールが来たときに「どの相手が漏らしたか」を特定する手がかりになります。必ず入力してください。"
            >
              <Input
                id="pa-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={PRIVATE_ADDRESS_LABEL_MAX}
                placeholder="例: 〇〇通販の会員登録"
                autoFocus
              />
            </Field>

            <Field label="届ける先" htmlFor="pa-mailbox" required>
              {mailboxes.isPending ? (
                <p className="text-sm text-muted-foreground">読み込んでいます…</p>
              ) : mailboxItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  先にメールアドレスを作成してください。
                </p>
              ) : (
                <Select value={mailboxId} onValueChange={setMailboxId}>
                  <SelectTrigger id="pa-mailbox">
                    <SelectValue placeholder="メールアドレスを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {mailboxItems.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="メモ（任意）" htmlFor="pa-note">
              <Textarea
                id="pa-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={PRIVATE_ADDRESS_NOTE_MAX}
                rows={2}
                placeholder="例: セール通知だけ受け取る"
              />
            </Field>

            <Disclosure summary="詳しい設定">
              <Field
                label="アドレスの長さ（文字数）"
                htmlFor="pa-length"
                hint={`${PRIVATE_ADDRESS_LOCAL_PART_MIN}〜${PRIVATE_ADDRESS_LOCAL_PART_MAX} 文字。長いほど推測されにくくなりますが、電話で読み上げるときに手間が増えます。`}
              >
                <Input
                  id="pa-length"
                  type="number"
                  min={PRIVATE_ADDRESS_LOCAL_PART_MIN}
                  max={PRIVATE_ADDRESS_LOCAL_PART_MAX}
                  value={length}
                  onChange={(e) => setLength(Number(e.target.value))}
                />
              </Field>
            </Disclosure>

            <SubmitBar
              submitLabel="発行する"
              pending={create.isPending}
              disabled={!labelOk || mailboxId === '' || remaining <= 0}
              onCancel={() => onOpenChange(false)}
              hint={remaining > 0 ? `あと ${formatCount(remaining)} 件発行できます` : undefined}
            />
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== 変更 */

/**
 * 変更できるのは**ラベルとメモだけ**。アドレスそのものは変えられない
 * （相手に渡した紙と食い違うため、API にも口が無い）。状態は一覧の
 * ボタンで変える。
 */
function EditDialog({ row, onClose }: { row: PrivateAddressView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = React.useState(row.label);
  const [note, setNote] = React.useState(row.note ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<PrivateAddressView>(`/private-addresses/${encodeURIComponent(row.id)}`, {
        label: label.trim(),
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.privateAddressesAll() });
      onClose();
    },
  });

  const labelOk = label.trim().length > 0;

  return (
    <Dialog open onOpenChange={(next) => (save.isPending ? undefined : !next && onClose())}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (labelOk) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{row.address} の設定</DialogTitle>
            <DialogDescription>
              渡した相手（ラベル）とメモを変更できます。アドレスそのものは変更できません。
            </DialogDescription>
          </DialogHeader>

          <FormAlert error={save.error} />

          <Field
            label="渡した相手（ラベル）"
            htmlFor="pa-edit-label"
            required
            error={labelOk ? undefined : '入力してください'}
          >
            <Input
              id="pa-edit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={PRIVATE_ADDRESS_LABEL_MAX}
              autoFocus
            />
          </Field>

          <Field label="メモ（任意）" htmlFor="pa-edit-note">
            <Textarea
              id="pa-edit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={PRIVATE_ADDRESS_NOTE_MAX}
              rows={2}
            />
          </Field>

          <SubmitBar
            submitLabel="保存する"
            pending={save.isPending}
            disabled={!labelOk}
            onCancel={onClose}
            hint="ラベルはWebメールの差出人候補の表示名にも使われます。"
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== 補助 */

/** 入力の落ち着きを待つ。IME の変換確定前に検索を投げると結果が踊る。 */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
